// ==========================================================================
// Video Thumbnail Loader Utility (Vimeo only)
// ==========================================================================

const VideoThumbnailLoader = {
  /**
   * Get thumbnail URL for Vimeo video
   * @param {string} videoId - Vimeo video ID
   * @returns {Promise<string>} Promise resolving to thumbnail URL
   */
  async getVimeoThumbnail(videoId) {
    try {
      const response = await fetch(`https://vimeo.com/api/v2/video/${videoId}.json`);
      const data = await response.json();
      // Return the largest thumbnail available
      return data[0].thumbnail_large || data[0].thumbnail_medium || data[0].thumbnail_small;
    } catch (error) {
      console.error('Error fetching Vimeo thumbnail:', error);
      return '';
    }
  },

  /**
   * Load thumbnail for a video element
   * @param {HTMLElement} element - Element with data-video-id
   */
  async loadThumbnail(element) {
    const videoId = element.getAttribute('data-video-id');

    if (!videoId) {
      console.warn('Missing video ID', element);
      return;
    }

    const thumbnailUrl = await this.getVimeoThumbnail(videoId);

    if (thumbnailUrl) {
      element.style.backgroundImage = `url('${thumbnailUrl}')`;
      element.style.backgroundSize = 'cover';
      element.style.backgroundPosition = 'center';
      element.style.backgroundRepeat = 'no-repeat';
      element.classList.add('video-thumbnail-loaded');
    }
  }
};

// ==========================================================================
// Video Player Manager (Vimeo only)
// ==========================================================================

const VideoPlayerManager = {
  // Store Vimeo player instances by element
  vimeoPlayers: new WeakMap(),

  /**
   * Create Vimeo player using VimeoPlayerComponent
   * @param {HTMLElement} container - Container element
   * @param {string} videoId - Vimeo video ID
   */
  createVimeoPlayer(container, videoId) {
    // Create a container for the Vimeo player component
    const playerContainer = document.createElement('div');
    playerContainer.classList.add('video-testimonial-player', 'video-testimonial-vimeo-player');
    container.appendChild(playerContainer);

    // Initialize VimeoPlayerComponent with autoplay
    if (window.VimeoPlayerComponent) {
      const vimeoPlayer = new window.VimeoPlayerComponent(playerContainer, {
        vimeoId: videoId,
        autoplay: true,
        muted: false,
        loop: false,
        showControls: true,
        posterUrl: null, // Don't show poster since we're auto-playing
        fillContainer: true // Fill the testimonial container
      });

      // Store the player instance for cleanup
      this.vimeoPlayers.set(container, vimeoPlayer);
    } else {
      // Fallback to iframe if VimeoPlayerComponent is not available
      console.warn('VimeoPlayerComponent not available, falling back to iframe');
      const iframe = document.createElement('iframe');
      iframe.setAttribute('src', `https://player.vimeo.com/video/${videoId}?autoplay=1`);
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
      iframe.setAttribute('allowfullscreen', '');
      playerContainer.appendChild(iframe);
    }
  },

  /**
   * Play video in container
   * @param {HTMLElement} element - Video card element
   */
  playVideo(element) {
    const videoId = element.getAttribute('data-video-id');

    if (!videoId) return;

    // Add playing class
    element.classList.add('video-playing');

    // Remove existing player if any
    const existingPlayer = element.querySelector('.video-testimonial-player');
    if (existingPlayer) {
      // Destroy Vimeo player instance if exists
      const vimeoInstance = this.vimeoPlayers.get(element);
      if (vimeoInstance) {
        vimeoInstance.destroy();
        this.vimeoPlayers.delete(element);
      }
      existingPlayer.remove();
    }

    // Small delay to allow fade out animation
    setTimeout(() => {
      this.createVimeoPlayer(element, videoId);
    }, 150);
  },

  /**
   * Stop video in container
   * @param {HTMLElement} element - Video card element
   */
  stopVideo(element) {
    // Handle Vimeo player cleanup
    const vimeoInstance = this.vimeoPlayers.get(element);
    if (vimeoInstance) {
      vimeoInstance.destroy();
      this.vimeoPlayers.delete(element);
    }

    const player = element.querySelector('.video-testimonial-player');
    if (player) {
      // Fade out player
      player.style.animation = 'fadeOutPlayer 0.3s ease forwards';

      // Remove after animation
      setTimeout(() => {
        player.remove();
      }, 300);
    }
    element.classList.remove('video-playing');
  }
};

// ==========================================================================
// Video Testimonials Slider
// ==========================================================================

class VideoTestimonialsSlider {
  constructor(container) {
    this.container = container;
    this.slider = container.querySelector('.video-testimonials__slider');

    if (!this.slider || typeof Splide === 'undefined') return;

    this.videoCount = parseInt(container.dataset.videoCount || '0');
    this.slidesPerPage = parseInt(container.dataset.slidesPerPage || '4');
    this.aspectRatio = container.dataset.aspectRatio || 'landscape';
    this.enableVideoModal = container.dataset.enableVideoModal === 'true';
    this.videoModalSize = container.dataset.videoModalSize || 'fullscreen';
    this.init();
    this.loadVideoThumbnails();
    this.setupVideoClickHandlers();
  }

  // Determine if slider should be enabled
  shouldEnableSlider() {
    const isMobile = window.innerWidth < 1024;
    const hasEnoughVideos = this.videoCount >= 5;
    return isMobile || hasEnoughVideos;
  }

  // Calculate perPage for tablet breakpoint
  getTabletPerPage() {
    // For tablet, use slidesPerPage - 1, but minimum of 2
    return Math.max(2, this.slidesPerPage - 1);
  }

  // Determine if peek should be used (only for portrait and square)
  shouldUsePeek() {
    return this.aspectRatio === 'portrait' || this.aspectRatio === 'square';
  }

  init() {
    const tabletPerPage = this.getTabletPerPage();
    const usePeek = this.shouldUsePeek();

    // Initialize slider (always mounted, but may be disabled)
    this.splideInstance = window.SplideUtils.initSplideWithControls(
      this.slider,
      this.container,
      {
        // Splide configuration
        type: 'carousel',
        rewind: true,
        perPage: this.slidesPerPage,
        perMove: 1,
        gap: '1.5rem',
        padding: { left: 0, right: 0 },
        arrows: false, // Using custom arrows
        pagination: false,
        trimSpace: 'move',
        drag: this.shouldEnableSlider(), // Conditionally enable drag
        speed: 600,
        breakpoints: {
          1024: {
            perPage: tabletPerPage,
            trimSpace: 'move',
          },
          767: {
            perPage: 2,
            gap: '1rem',
            trimSpace: 'move',
            padding: usePeek ? { right: '22%' } : { left: 0, right: 0 },
            drag: true, // Always enabled on mobile
          },
          576: {
            perPage: 1,
            gap: '1rem',
            padding: usePeek ? { right: '22%' } : { left: 0, right: 0 },
            trimSpace: 'move',
            drag: true, // Always enabled on mobile
          }
        },
        // Accessibility
        i18n: {
          prev: 'Previous testimonial',
          next: 'Next testimonial',
          first: 'Go to first testimonial',
          last: 'Go to last testimonial',
          slideX: 'Go to testimonial %s',
          pageX: 'Go to page %s',
          play: 'Start autoplay',
          pause: 'Pause autoplay',
        },
      },
      {
        // Enable custom arrows
        customArrows: true,
        // Enable scrollbar
        scrollbar: true,
        // Enable tabindex management for accessibility
        tabindex: true
      }
    );

    // Initial setup
    this.updateSlider();

    // Track current slide index for video stop logic
    this.lastKnownSlideIndex = this.splideInstance.index;

    // Use 'move' event (before animation) to capture the target index
    // and 'moved' event (after animation) to stop videos only if index changed
    this.splideInstance.on('move', (newIndex) => {
      // Only stop videos if we're actually moving to a different slide
      if (newIndex !== this.lastKnownSlideIndex) {
        this.stopAllVideos();
        this.lastKnownSlideIndex = newIndex;
      }
    });

    // Handle resize
    this.resizeTimeout = null;
    this.handleResize = () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => this.updateSlider(), 250);
    };
    window.addEventListener('resize', this.handleResize);
  }

  // Update slider state based on conditions
  updateSlider() {
    if (!this.splideInstance) return;

    const shouldEnable = this.shouldEnableSlider();

    if (shouldEnable) {
      this.container.classList.add('slider-active');
      // Enable drag on desktop
      this.splideInstance.options.drag = true;
      this.splideInstance.refresh();
    } else {
      this.container.classList.remove('slider-active');
      // Disable drag on desktop when not enough videos
      this.splideInstance.options.drag = false;
      // Go to first slide when disabling
      this.splideInstance.go(0);
      this.splideInstance.refresh();
    }
  }

  /**
   * Load thumbnails for all video elements in the slider
   */
  loadVideoThumbnails() {
    const videoElements = this.container.querySelectorAll('[data-video-thumbnail-loader]');
    videoElements.forEach(element => {
      VideoThumbnailLoader.loadThumbnail(element);
    });
  }

  /**
   * Stop all playing videos
   */
  stopAllVideos() {
    const allVideoCards = this.container.querySelectorAll('[data-video-activator]');
    allVideoCards.forEach(card => {
      VideoPlayerManager.stopVideo(card);
    });
  }

  /**
   * Setup handler to reset video when focus is lost
   * @param {HTMLElement} card - Video card element
   */
  setupVideoResetOnBlur(card) {
    // Use a slight delay to check if focus moved outside the video player
    const checkFocusLoss = () => {
      setTimeout(() => {
        const playerContainer = card.querySelector('.video-testimonial-player');
        if (!playerContainer) return;

        // Check if the currently focused element is outside the player container
        const focusedElement = document.activeElement;
        const isFocusInPlayer = playerContainer.contains(focusedElement);
        const isFocusOnCard = card.contains(focusedElement) && focusedElement === card;

        // If focus is not in the player and not on the card itself, reset the video
        if (!isFocusInPlayer && !isFocusOnCard) {
          VideoPlayerManager.stopVideo(card);
        }
      }, 100);
    };

    // Listen for focusout on the card
    card.addEventListener('focusout', checkFocusLoss);

    // Also listen for clicks outside the player
    const handleClickOutside = (e) => {
      const playerContainer = card.querySelector('.video-testimonial-player');
      if (!playerContainer) return;

      // If click is outside the card, stop the video
      if (!card.contains(e.target)) {
        VideoPlayerManager.stopVideo(card);
        // Remove this listener once video is stopped
        document.removeEventListener('click', handleClickOutside);
      }
    };

    // Add click outside listener after a brief delay to avoid immediate trigger
    setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);
  }

  /**
   * Setup click handlers for video cards
   */
  setupVideoClickHandlers() {
    const videoCards = this.container.querySelectorAll('[data-video-activator]');

    // Track if last interaction was keyboard (for focus handling)
    let lastInteractionWasKeyboard = false;

    // Detect keyboard vs mouse interaction
    this.container.addEventListener('mousedown', () => {
      lastInteractionWasKeyboard = false;
    });

    this.container.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        lastInteractionWasKeyboard = true;
      }
    });

    videoCards.forEach((card, index) => {
      // Track drag vs click
      let startX = 0;
      let startY = 0;
      let isDrag = false;

      card.addEventListener('mousedown', (e) => {
        // Don't track for vimeo player area
        if (e.target.closest('.vimeo-player') || e.target.closest('.video-testimonial-vimeo-player')) {
          return;
        }
        startX = e.clientX;
        startY = e.clientY;
        isDrag = false;
      });

      card.addEventListener('mousemove', (e) => {
        // If mouse moved more than 10px, consider it a drag
        if (Math.abs(e.clientX - startX) > 10 || Math.abs(e.clientY - startY) > 10) {
          isDrag = true;
        }
      });

      // Click handler
      card.addEventListener('click', (e) => {
        // Don't interfere with clicks on the Vimeo player controls
        if (e.target.closest('.vimeo-player') || e.target.closest('.video-testimonial-vimeo-player')) {
          return;
        }

        // If it was a drag (slider swipe), don't toggle video
        if (isDrag) {
          isDrag = false;
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        const videoId = card.getAttribute('data-video-id');

        // If video modal is enabled and VideoModal is available, open in modal
        if (this.enableVideoModal && window.VideoModal && videoId) {
          window.VideoModal.open({
            type: 'vimeo',
            vimeoId: videoId,
            size: this.videoModalSize,
            aspectRatio: this.aspectRatio,
            triggerElement: card
          });
          return;
        }

        // Otherwise, play inline
        // Don't stop video if already playing - let it continue playing
        if (!card.classList.contains('video-playing')) {
          // Stop any other playing videos
          const allVideoCards = this.container.querySelectorAll('[data-video-activator]');
          allVideoCards.forEach(otherCard => {
            if (otherCard !== card) {
              VideoPlayerManager.stopVideo(otherCard);
            }
          });

          // Play this video
          VideoPlayerManager.playVideo(card);

          // Setup focus loss handler to reset video
          this.setupVideoResetOnBlur(card);
        }
      }, true);

      // Keyboard accessibility
      card.setAttribute('tabindex', '0');
      card.setAttribute('role', 'button');
      card.setAttribute('aria-label', `Play video testimonial ${index + 1}`);

      // Focus handler - only move slider for keyboard navigation
      card.addEventListener('focus', () => {
        if (lastInteractionWasKeyboard && this.splideInstance) {
          this.splideInstance.go(index);
        }
      });

      // Enter/Space to activate
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          card.click();
        }
      });
    });
  }

  destroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.splideInstance) {
      this.splideInstance.destroy();
    }

    // Clean up video players
    this.stopAllVideos();
  }
}

// ==========================================================================
// Initialize all video testimonials sliders on the page
// ==========================================================================

document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('[id^="VideoTestimonials-"]');
  containers.forEach(container => {
    const slider = container.querySelector('.video-testimonials__slider');
    if (slider) {
      container.sliderInstance = new VideoTestimonialsSlider(container);
    }
  });
});

// ==========================================================================
// Shopify Theme Editor Support
// ==========================================================================

if (typeof window.SplideUtils !== 'undefined') {
  window.SplideUtils.setupThemeEditorEvents(
    'VideoTestimonials-',
    '.video-testimonials__slider',
    (sectionElement) => {
      const container = sectionElement.querySelector('[id^="VideoTestimonials-"]');
      if (container) {
        const slider = container.querySelector('.video-testimonials__slider');
        if (slider) {
          if (container.sliderInstance) {
            container.sliderInstance.destroy();
          }
          container.sliderInstance = new VideoTestimonialsSlider(container);
        }
      }
    }
  );
}
